import { z } from 'zod'
import type { Erc20Transfer, TransactionIndex } from '../types'
import type { InternalTransactionWithIndex } from '../types/internalTransaction'
import { addressPrimitiveSchema, lowerHexSchema } from '../types/primitive'
import { requestExplorerJson } from './request'
import type { Explorer, HeadersInput } from './types'

/** NodeReal が返す十六進整数を検証する。 */
const integerSchema = z
  .string()
  .regex(/^0x[0-9a-f]+$/i)
  .transform(BigInt)
/** 履歴一覧に共通する確定済み取引。種類固有の値は利用時に検証する。 */
const activitySchema = z.object({
  blockNum: integerSchema,
  hash: lowerHexSchema,
  blockTimeStamp: z.number().int(),
  from: addressPrimitiveSchema,
  to: z.union([addressPrimitiveSchema, z.literal('')]),
  input: lowerHexSchema.optional(),
  value: integerSchema.optional(),
  gas: z.number().int().nonnegative().optional(),
  receiptsStatus: z.number().optional(),
  contractAddress: addressPrimitiveSchema.optional(),
  logIndex: z.number().int().optional(),
})
/** API のエラーと欠落ページを成功として扱わないための応答契約。 */
const pageSchema = z.object({
  code: z.literal(0),
  data: z.object({
    list: z.array(activitySchema),
    total: z.number().int().nonnegative(),
  }),
})

/**
 * Etherscan の対応が終了した opBNB の住所履歴を公式 NodeReal API に切り替える。
 * @param params 既存 Explorer と認証プロキシ。履歴以外の機能は既存実装を保持する。
 * @returns ページングと block 範囲に対応した Explorer。
 */
export const createNodeRealExplorer = ({
  fallback,
  proxyUrl,
}: { fallback: Explorer; proxyUrl?: string }): Explorer => {
  const origin = 'https://op-bnb-mainnet-explorer-api.nodereal.io'
  /** 指定種別の全ページを取得し、対象範囲内だけを返す。 */
  const getActivities = async ({
    address,
    fromBlock = 0,
    toBlock = Number.MAX_SAFE_INTEGER,
    headers,
    type,
  }: {
    address: string
    fromBlock?: number
    toBlock?: number
    headers?: HeadersInput
    type: string
  }) => {
    const items: z.infer<typeof activitySchema>[] = []
    const seenPages = new Set<string>()
    const pageSize = 100
    for (let page = 1; ; page += 1) {
      const url = new URL(
        type === 'internal'
          ? '/api/tx/getInternalTxList'
          : '/api/tx/getAssetTransferByAddress',
        origin,
      )
      url.search = new URLSearchParams({
        address,
        type,
        page: String(page),
        pageSize: String(pageSize),
        order: 'desc',
      }).toString()
      const result = pageSchema.parse(
        await requestExplorerJson({
          url:
            proxyUrl === undefined
              ? url.toString()
              : `${proxyUrl}${encodeURIComponent(url.toString())}`,
          key: origin,
          intervalMs: 500,
          headers: typeof headers === 'function' ? await headers() : headers,
        }),
      )
      const signature = JSON.stringify(
        result.data.list.map((item) => [item.hash, item.logIndex]),
      )
      if (result.data.list.length > 0 && seenPages.has(signature))
        throw new Error('NodeReal pagination did not advance')
      seenPages.add(signature)
      for (const item of result.data.list)
        if (
          item.blockNum >= BigInt(fromBlock) &&
          item.blockNum <= BigInt(toBlock)
        )
          items.push(item)
      if (
        page * pageSize >= result.data.total ||
        (result.data.list.length > 0 &&
          result.data.list.every((item) => item.blockNum < BigInt(fromBlock)))
      )
        return items
      if (result.data.list.length === 0)
        throw new Error('NodeReal returned an incomplete history page')
    }
  }
  /** 履歴を解析に必要な取引 index に変換する。 */
  const toIndex = (item: z.infer<typeof activitySchema>): TransactionIndex => ({
    hash: item.hash,
    blockNumber: Number(item.blockNum),
    timestamp: item.blockTimeStamp * 1000,
    input: item.input,
    value: item.value,
  })
  return {
    ...fallback,
    name: 'opBNB NodeReal Explorer',
    baseUrl: 'https://opbnbscan.com',
    getAddressTransactionIndexes: async (params) =>
      (await getActivities({ ...params, type: 'transaction' })).map(toIndex),
    getAddressInternalTransactions: async (
      params,
    ): Promise<InternalTransactionWithIndex[]> =>
      (await getActivities({ ...params, type: 'internal' })).map((item) => ({
        txHash: item.hash,
        blockNumber: Number(item.blockNum),
        timestamp: item.blockTimeStamp * 1000,
        from: item.from,
        to: item.to === '' ? undefined : item.to,
        gas: BigInt(z.number().int().nonnegative().parse(item.gas)),
        isError: z.number().parse(item.receiptsStatus) !== 1,
        value: z.bigint().parse(item.value),
      })),
    getAddressTokenTransferIndexes: async (params) => {
      const indexes = new Map<string, TransactionIndex>()
      const erc20Transfers: Erc20Transfer[] = []
      for (const type of ['20', '721', '1155']) {
        for (const item of await getActivities({ ...params, type })) {
          indexes.set(item.hash, toIndex(item))
          if (type === '20')
            erc20Transfers.push({
              address: addressPrimitiveSchema.parse(item.contractAddress),
              from: item.from,
              to: addressPrimitiveSchema.parse(item.to),
              logIndex: z.number().int().parse(item.logIndex),
              value: z.bigint().parse(item.value),
            })
        }
      }
      return { indexes: [...indexes.values()], erc20Transfers }
    },
  }
}
